/**
 * Compiling a resolved Dispatch query to the one predicate tree the driver-query engine evaluates.
 *
 * The controls in force, the ad-hoc conditions, and the free-text search become one `FilterNode`. Pure and
 * browser-safe, with no knowledge of where the rows come from: a page projects its rows to records and hands the tree
 * to `matchesFilter`, and a server may do the same with a projector of its own.
 */

import type { FilterNode } from "@storyboard/driver-query/predicate";
import type { ResolvedDispatchQuery } from "./resolve";
import type { DispatchControl, DispatchControlValue, DispatchField } from "./view";
import { fieldRefOf } from "./view";

/**
 * The live value of every control in a view, keyed by control id.
 *
 * Whether a toggle is on, the values a picker holds, or the minutes a time control spans (`0` meaning off).
 */
export type DispatchControlValues = Record<string, DispatchControlValue>;

/**
 * Whether a predicate reads the clock anywhere: an `olderThan` or `newerThan` leaf, at any depth.
 */
export function hasTimeOperator(node: FilterNode): boolean {
  if ("and" in node) {
    return node.and.some(hasTimeOperator);
  }
  if ("or" in node) {
    return node.or.some(hasTimeOperator);
  }
  if ("not" in node) {
    return hasTimeOperator(node.not);
  }
  return "olderThan" in node || "newerThan" in node;
}

/**
 * Escapes a search term so it matches literally inside a `regex` leaf.
 */
export function escapeRegExp(term: string): string {
  return term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * The predicate for "this field is truthy": it has a value, and the value is not the `"false"` a boolean kind projects.
 *
 * Strings count when non-blank, tag columns when they carry any value, booleans when `"true"`. So a toggle over a tag
 * such as "vehicle" reads as "has a vehicle assigned", and the same toggle over `unread` reads as "is unread".
 */
export function truthyNode(field: DispatchField): FilterNode {
  const ref = fieldRefOf(field);
  return { and: [{ not: { field: ref, isEmpty: {} } }, { not: { field: ref, eq: "false" } }] };
}

/**
 * The predicate one control contributes for its current value, or `null` when it is off or empty.
 */
export function controlNode(control: DispatchControl, value: DispatchControlValue | undefined): FilterNode | null {
  switch (control.kind) {
    case "toggle": {
      if (value !== true) {
        return null;
      }
      if (control.predicate) {
        return control.predicate;
      }
      return control.field ? truthyNode(control.field) : null;
    }
    case "values": {
      const values = Array.isArray(value) ? value : [];
      return values.length > 0 ? { field: fieldRefOf(control.field), inArray: values } : null;
    }
    case "time":
      return typeof value === "number" && value > 0
        ? { field: fieldRefOf(control.field), newerThan: { minutes: value } }
        : null;
  }
}

/**
 * Compiles a resolved query and the transient search term to one predicate, or `null` when nothing narrows.
 *
 * An `and` of every active control (see {@link controlNode}), every ad-hoc condition, and, for a non-blank
 * search, an `or` of case-insensitive literal `regex` leaves over the query's search fields. `matchesFilter` reads the
 * `null` as match-all.
 */
export function compileDispatchFilter(
  query: Pick<ResolvedDispatchQuery, "controls" | "controlValues" | "search" | "adhoc">,
  search: string,
): FilterNode | null {
  const leaves: FilterNode[] = [];
  for (const control of query.controls) {
    const leaf = controlNode(control, query.controlValues[control.id]);
    if (leaf) {
      leaves.push(leaf);
    }
  }
  leaves.push(...query.adhoc);

  const term = search.trim();
  if (term !== "" && query.search.length > 0) {
    const pattern = escapeRegExp(term);
    leaves.push({ or: query.search.map((field) => ({ field: fieldRefOf(field), regex: { pattern, flags: "i" } })) });
  }

  if (leaves.length === 0) {
    return null;
  }
  const [only] = leaves;
  return leaves.length === 1 && only !== undefined ? only : { and: leaves };
}
